import { formatCaseStatus } from '../../../helpers/formatCaseStatus';
import { getLatestTask } from './latestTask';

export type CaseStatusFilter = 'Case In Progress' | 'Case For Dentist Approval' | 'Completed' | 'Finalizing Design';

/** "scheduled" is a date someone set; "tat" and "standard" are estimates the Hub computed. */
export type ExpectedReturnSource = 'scheduled' | 'tat' | 'standard';

export interface CaseFlags {
  redesign?: boolean;
  rescan?: boolean;
  rescanUploaded?: boolean;
  withApproval?: boolean;
}

/** The subset of a Hub case row that the chatbot talks about. */
export interface CaseSummary {
  id: string;
  refId?: number | string;
  caseRefNo: string;
  patientName: string;
  treatments: string[];
  rawStatus: string;
  statusLabel: string;
  latestProvider?: string;
  latestStatusLabel?: string;
  lastUpdated?: string;
  /** When the dentist sent the case (the case's createdAt). */
  sentDate?: string;
  /** The stored return date (prebook appointment, DIO/add-on, fabricate transition); a 0001-01-01 placeholder when none. */
  returnDate?: string;
  /** What the Hub worked out the case is due (api/chat/case-timing): the stored date, else the fabrication partner's TAT, else the standard design turnaround. */
  expectedReturnDate?: string;
  expectedReturnSource?: ExpectedReturnSource;
  outputType?: string;
  flags?: CaseFlags;
}

export interface PracticeSelection {
  practiceId: string;
  personnelIds: string[];
}

const SELECTED_PRACTICE_KEY = 'locSelectedPractice';

/** Every practice on the user's profile, each with its personnel ids. */
export function allPractices(userInfo: any): PracticeSelection[] {
  const practices: Array<{ practiceId: string; personnelId: string }> = userInfo?.PracticeWithPersonnel || [];
  const byPractice = new Map<string, string[]>();
  practices.forEach((p) => {
    if (!p?.practiceId) return;
    const ids = byPractice.get(p.practiceId) || [];
    if (p.personnelId && !ids.includes(p.personnelId)) ids.push(p.personnelId);
    byPractice.set(p.practiceId, ids);
  });
  return Array.from(byPractice, ([practiceId, personnelIds]) => ({ practiceId, personnelIds }));
}

/** Same practice resolution the Cases page uses: the practice picked in the UI, else the first one. */
export function resolvePractice(userInfo: any, storage: Pick<Storage, 'getItem'> | null = safeStorage()): PracticeSelection | null {
  const practices: Array<{ practiceId: string; personnelId: string }> = userInfo?.PracticeWithPersonnel || [];
  if (practices.length === 0) return null;

  let stored: string | null = null;
  try { stored = storage ? storage.getItem(SELECTED_PRACTICE_KEY) : null; } catch {}

  const practiceId = practices.some((p) => p.practiceId === stored) ? (stored as string) : practices[0].practiceId;
  const personnelIds = practices.filter((p) => p.practiceId === practiceId).map((p) => p.personnelId);
  return { practiceId, personnelIds };
}

function safeStorage(): Pick<Storage, 'getItem'> | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

const labelFor = (status: string | undefined, fallback?: string): string => {
  const label = status ? formatCaseStatus(status) : 'None';
  if (label !== 'None') return label;
  return fallback || status || 'Unknown';
};

export function toCaseSummary(row: any): CaseSummary {
  const latest = getLatestTask(row);
  const treatmentsFromDetail: string[] = (row?.caseTreatments || [])
    .map((t: any) => t?.treatmentType)
    .filter(Boolean);
  const treatments = treatmentsFromDetail.length > 0
    ? treatmentsFromDetail
    : (Array.isArray(row?.treatment) ? row.treatment.filter(Boolean) : []);

  const rawStatus: string = latest?.status || row?.statusDescription || '';
  const patientName = [row?.patientFirstName, row?.patientLastName].filter(Boolean).join(' ').trim();

  return {
    id: String(row?.id ?? ''),
    refId: row?.refId,
    caseRefNo: String(row?.caseRefNo ?? ''),
    patientName,
    treatments,
    rawStatus,
    statusLabel: labelFor(rawStatus, row?.statusDescription),
    latestProvider: latest?.serviceProvider || undefined,
    latestStatusLabel: latest?.status ? labelFor(latest.status) : undefined,
    lastUpdated: latest?.lastModified || row?.updatedAt || undefined,
    sentDate: row?.createdAt || undefined,
    returnDate: row?.returnDate || undefined,
    outputType: row?.outputType || undefined,
    flags: row?.flags || undefined,
  };
}
